// Service pour générer un PDF via l'impression du navigateur avec support multilingue

export const generatePDF = async (diagnostic, analysisResults, globalScore, executiveSummary, recommendations, lang = 'fr', t) => {
  // Charger les logos et les convertir en base64
  const logoBelanBase64 = await imageToBase64('/logo-belan.jpg');
  const logoProjectBoostBase64 = await imageToBase64('/logo-projectboost.png');
  
  // Créer une nouvelle fenêtre pour l'impression
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert(t ? t('allowPopups') : 'Veuillez autoriser les pop-ups pour télécharger le PDF');
    return;
  }

  // Générer le HTML pour l'impression
  const htmlContent = generatePrintableHTML(diagnostic, analysisResults, globalScore, executiveSummary, recommendations, lang, t, logoBelanBase64, logoProjectBoostBase64);
  
  // Écrire le contenu dans la nouvelle fenêtre
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Attendre que tout soit chargé puis lancer l'impression
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
};

// Fonction pour convertir une image en base64
const imageToBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => {
      console.error('Erreur chargement image:', url);
      resolve(''); // Retourner vide si erreur
    };
    img.src = url;
  });
};

const generatePrintableHTML = (diagnostic, analysisResults, globalScore, executiveSummary, recommendations, lang, t, logoBelan, logoProjectBoost) => {
  const criticalAndFragileAxes = analysisResults.filter(
    axis => axis.classification.level === 'critical' || axis.classification.level === 'fragile'
  );

  // Textes traduits
  const texts = {
    fr: {
      diagnosticReport: 'Rapport de Diagnostic',
      healYourProject: 'Soignez votre projet',
      organization: 'Organisation',
      project: 'Projet',
      projectManager: 'Chef de projet',
      date: 'Date',
      participants: 'Participants',
      people: 'personnes',
      executiveSummary: 'Résumé exécutif',
      projectHealthLevel: 'Niveau de santé global du projet',
      globalScore: 'Score global',
      majorFindings: 'Constats majeurs',
      priorityRisks: 'Risques prioritaires',
      risk: 'Risque',
      immediateDecisions: 'Décisions recommandées immédiatement',
      decision: 'Décision',
      methodology: 'Méthodologie du diagnostic',
      toolUsed: 'Outil utilisé',
      structuredQuestionnaire: 'Questionnaire structuré à 6 axes',
      numberOfRespondents: 'Nombre de répondants',
      analysisMode: 'Mode d\'analyse',
      quantitativeQualitative: 'Quantitatif (scores) et qualitatif (causes / conséquences / actions)',
      methodologyNote: 'Ce diagnostic n\'est pas un audit de conformité mais un outil d\'aide à la décision et à l\'amélioration de la performance globale du projet.',
      numericalResults: 'Résultats chiffrés du diagnostic',
      axis: 'Axe',
      averageScore: 'Score moyen',
      assessment: 'Appréciation',
      scoreVisualization: 'Visualisation des scores par axe',
      globalReading: 'Lecture globale',
      criticalAxes: 'Axes critiques (< 3)',
      fragileAxes: 'Axes fragiles (3 – 4)',
      masteredAxes: 'Axes maîtrisés (> 4)',
      none: 'Aucun',
      dysfunctionAnalysis: 'Analyse des dysfonctionnements',
      dysfunctionIntro: 'Cette section relie les scores observés aux problèmes concrets vécus sur le projet.',
      observedProblems: 'Problèmes observés',
      projectImpacts: 'Impacts projet',
      syntheticAnalysis: 'Analyse synthétique',
      whatBlocksPerformance: 'Ce qui bloque réellement la performance du projet',
      noMajorBlockage: 'Aucun blocage majeur identifié',
      whatGeneratesStress: 'Ce qui génère du stress et de la perte d\'énergie',
      climateMastered: 'Climat globalement maîtrisé',
      whatDelaysDecisions: 'Ce qui retarde les décisions',
      functionalGovernance: 'Gouvernance fonctionnelle',
      detailedQualitativeAnalysis: 'Analyse qualitative détaillée',
      qualitativeAnalysisIntro: 'Pour chaque axe critique ou fragile, analyse approfondie des causes racines et actions correctives.',
      rootCauseNote: 'La colonne "Cause racine" est à remplir par le chef de projet et son équipe lors de l\'analyse approfondie.',
      keyQuestion: 'Question clé',
      rootCause: 'Cause racine',
      toDefine: 'À définir',
      observableConsequence: 'Conséquence observable',
      correctiveAction: 'Action corrective prioritaire',
      prioritizedActionPlan: 'Plan d\'actions priorisé',
      actionPlanPrinciple: 'Principe : peu d\'actions, mais des actions structurantes.',
      responsibleDeadlineNote: 'Les colonnes "Responsable" et "Échéance" sont à remplir par le chef de projet lors de la mise en œuvre.',
      priorityAction: 'Action prioritaire',
      responsible: 'Responsable',
      deadline: 'Échéance',
      successIndicator: 'Indicateur de succès (À adapter si nécessaire)',
      expectedBenefits: 'Bénéfices attendus après mise en œuvre',
      priorityClarification: 'Clarification des priorités',
      teamAligned: 'Équipe alignée sur les objectifs essentiels',
      fasterDecisions: 'Décisions plus rapides',
      streamlinedProcess: 'Processus décisionnel fluidifié',
      productiveMeetings: 'Réunions productives',
      optimizedCollectiveTime: 'Temps collectif optimisé',
      betterVisibility: 'Meilleure visibilité',
      clearSharedProgress: 'Avancement clair et partagé',
      reducedStress: 'Stress réduit',
      calmWorkEnvironment: 'Climat de travail apaisé',
      restoredPerformance: 'Performance restaurée',
      measurableGains: 'Gain de temps et d\'énergie mesurable',
      finalRecommendations: 'Recommandations finales',
      recommendedFollowUpRhythm: 'Rythme de suivi recommandé',
      monthly: 'Mensuel',
      nextDiagnosticReview: 'Prochaine revue de diagnostic',
      toFillByPM: '(à remplir par le chef de projet)',
      successConditions: 'Conditions de succès',
      topManagementCommitment: 'Engagement du top management',
      pilotingDiscipline: 'Discipline de pilotage',
      decisionClarity: 'Clarté des décisions',
      conclusion: 'Conclusion',
      conclusionText1: 'Ce diagnostic fournit une lecture objective et partagée de la situation du projet.',
      conclusionText2: 'Il constitue une base solide pour reprendre le contrôle, sécuriser les décisions et restaurer la performance.',
      mastered: 'Maîtrisé (≥ 4,1)',
      fragile: 'Fragile (3,0 - 4,0)',
      critical: 'Critique (< 3,0)'
    },
    en: {
      diagnosticReport: 'Diagnostic Report',
      healYourProject: 'Heal your project',
      organization: 'Organization',
      project: 'Project',
      projectManager: 'Project Manager',
      date: 'Date',
      participants: 'Participants',
      people: 'people',
      executiveSummary: 'Executive Summary',
      projectHealthLevel: 'Overall project health level',
      globalScore: 'Global score',
      majorFindings: 'Major findings',
      priorityRisks: 'Priority risks',
      risk: 'Risk',
      immediateDecisions: 'Immediately recommended decisions',
      decision: 'Decision',
      methodology: 'Diagnostic methodology',
      toolUsed: 'Tool used',
      structuredQuestionnaire: 'Structured 6-axis questionnaire',
      numberOfRespondents: 'Number of respondents',
      analysisMode: 'Analysis mode',
      quantitativeQualitative: 'Quantitative (scores) and qualitative (causes / consequences / actions)',
      methodologyNote: 'This diagnostic is not a compliance audit but a decision-support and overall project performance improvement tool.',
      numericalResults: 'Numerical diagnostic results',
      axis: 'Axis',
      averageScore: 'Average score',
      assessment: 'Assessment',
      scoreVisualization: 'Score visualization by axis',
      globalReading: 'Global reading',
      criticalAxes: 'Critical axes (< 3)',
      fragileAxes: 'Fragile axes (3 – 4)',
      masteredAxes: 'Mastered axes (> 4)',
      none: 'None',
      dysfunctionAnalysis: 'Dysfunction analysis',
      dysfunctionIntro: 'This section links observed scores to concrete problems experienced in the project.',
      observedProblems: 'Observed problems',
      projectImpacts: 'Project impacts',
      syntheticAnalysis: 'Synthetic analysis',
      whatBlocksPerformance: 'What actually blocks project performance',
      noMajorBlockage: 'No major blockage identified',
      whatGeneratesStress: 'What generates stress and energy loss',
      climateMastered: 'Climate generally under control',
      whatDelaysDecisions: 'What delays decisions',
      functionalGovernance: 'Functional governance',
      detailedQualitativeAnalysis: 'Detailed qualitative analysis',
      qualitativeAnalysisIntro: 'For each critical or fragile axis, in-depth analysis of root causes and corrective actions.',
      rootCauseNote: 'The "Root cause" column is to be filled in by the project manager and their team during in-depth analysis.',
      keyQuestion: 'Key question',
      rootCause: 'Root cause',
      toDefine: 'To define',
      observableConsequence: 'Observable consequence',
      correctiveAction: 'Priority corrective action',
      prioritizedActionPlan: 'Prioritized action plan',
      actionPlanPrinciple: 'Principle: few actions, but structural actions.',
      responsibleDeadlineNote: 'The "Responsible" and "Deadline" columns are to be filled in by the project manager during implementation.',
      priorityAction: 'Priority action',
      responsible: 'Responsible',
      deadline: 'Deadline',
      successIndicator: 'Success indicator (To adapt if necessary)',
      expectedBenefits: 'Expected benefits after implementation',
      priorityClarification: 'Priority clarification',
      teamAligned: 'Team aligned on essential objectives',
      fasterDecisions: 'Faster decisions',
      streamlinedProcess: 'Streamlined decision-making process',
      productiveMeetings: 'Productive meetings',
      optimizedCollectiveTime: 'Optimized collective time',
      betterVisibility: 'Better visibility',
      clearSharedProgress: 'Clear and shared progress',
      reducedStress: 'Reduced stress',
      calmWorkEnvironment: 'Calm work environment',
      restoredPerformance: 'Restored performance',
      measurableGains: 'Measurable time and energy gains',
      finalRecommendations: 'Final recommendations',
      recommendedFollowUpRhythm: 'Recommended follow-up rhythm',
      monthly: 'Monthly',
      nextDiagnosticReview: 'Next diagnostic review',
      toFillByPM: '(to be filled in by project manager)',
      successConditions: 'Success conditions',
      topManagementCommitment: 'Top management commitment',
      pilotingDiscipline: 'Piloting discipline',
      decisionClarity: 'Decision clarity',
      conclusion: 'Conclusion',
      conclusionText1: 'This diagnostic provides an objective and shared reading of the project situation.',
      conclusionText2: 'It constitutes a solid foundation for regaining control, securing decisions and restoring performance.',
      mastered: 'Mastered (≥ 4.1)',
      fragile: 'Fragile (3.0 - 4.0)',
      critical: 'Critical (< 3.0)'
    }
  };

  const tx = texts[lang] || texts.fr;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${tx.diagnosticReport} - ${diagnostic.projectName}</title>
  <style>
    @page {
      size: A4;
      margin: 2cm;
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      font-size: 11pt;
    }
    
    .page-break {
      page-break-after: always;
    }
    
    h1 {
      color: #0284c7;
      font-size: 24pt;
      margin-bottom: 10pt;
      text-align: center;
    }
    
    h2 {
      color: #0284c7;
      font-size: 18pt;
      margin-top: 20pt;
      margin-bottom: 10pt;
      border-bottom: 2pt solid #0284c7;
      padding-bottom: 5pt;
    }
    
    h3 {
      color: #334155;
      font-size: 14pt;
      margin-top: 15pt;
      margin-bottom: 8pt;
    }
    
    h4 {
      color: #475569;
      font-size: 12pt;
      margin-top: 10pt;
      margin-bottom: 6pt;
    }
    
    p {
      margin-bottom: 8pt;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 15pt 0;
      font-size: 10pt;
    }
    
    th, td {
      border: 1pt solid #cbd5e1;
      padding: 8pt;
      text-align: left;
    }
    
    th {
      background-color: #f1f5f9;
      font-weight: bold;
    }
    
    tr:nth-child(even) {
      background-color: #f8fafc;
    }
    
    .cover-page {
      text-align: center;
      padding-top: 100pt;
    }
    
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10pt;
      margin: 20pt 0;
      padding: 15pt;
      background-color: #f8fafc;
      border-radius: 8pt;
    }
    
    .info-item {
      margin-bottom: 8pt;
    }
    
    .info-label {
      font-size: 9pt;
      color: #64748b;
      margin-bottom: 2pt;
    }
    
    .info-value {
      font-weight: bold;
      color: #1e293b;
    }
    
    .score-box {
      text-align: center;
      padding: 20pt;
      background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
      border-radius: 8pt;
      margin: 15pt 0;
    }
    
    .score-value {
      font-size: 48pt;
      font-weight: bold;
      color: #0284c7;
    }
    
    .alert-box {
      padding: 12pt;
      border-radius: 6pt;
      margin: 15pt 0;
    }
    
    .alert-yellow {
      background-color: #fef3c7;
      border-left: 4pt solid #f59e0b;
    }
    
    .alert-red {
      background-color: #fee2e2;
      border-left: 4pt solid #ef4444;
    }
    
    .alert-blue {
      background-color: #dbeafe;
      border-left: 4pt solid #3b82f6;
    }
    
    .alert-green {
      background-color: #d1fae5;
      border-left: 4pt solid #10b981;
    }
    
    ul, ol {
      margin-left: 20pt;
      margin-bottom: 10pt;
    }
    
    li {
      margin-bottom: 5pt;
    }
    
    .highlight-yellow {
      background-color: #fef3c7;
      font-style: italic;
      color: #92400e;
    }
    
    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>

  <!-- PAGE DE GARDE -->
  <div class="cover-page">
    <!-- Logos en haut -->
    ${logoBelan && logoProjectBoost ? `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 40pt; padding: 0 20pt;">
      <img src="${logoBelan}" alt="BEL-AN Prod" style="height: 60pt; object-fit: contain;" />
      <img src="${logoProjectBoost}" alt="ProjectBoost" style="height: 60pt; object-fit: contain;" />
    </div>
    ` : ''}

    <h1>${tx.diagnosticReport}</h1>
    <h2 style="border: none; margin-top: 20pt;">${tx.healYourProject}</h2>
    
    <div class="info-grid" style="margin-top: 50pt; text-align: left;">
      <div class="info-item">
        <div class="info-label">${tx.organization}</div>
        <div class="info-value">${diagnostic.organization}</div>
      </div>
      <div class="info-item">
        <div class="info-label">${tx.project}</div>
        <div class="info-value">${diagnostic.projectName}</div>
      </div>
      <div class="info-item">
        <div class="info-label">${tx.projectManager}</div>
        <div class="info-value">${diagnostic.projectManager}</div>
      </div>
      <div class="info-item">
        <div class="info-label">${tx.date}</div>
        <div class="info-value">${new Date(diagnostic.completedAt?.toDate ? diagnostic.completedAt.toDate() : diagnostic.completedAt).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR')}</div>
      </div>
      <div class="info-item">
        <div class="info-label">${tx.participants}</div>
        <div class="info-value">${diagnostic.participants} ${tx.people}</div>
      </div>
    </div>
  </div>

  <div class="page-break"></div>

  <!-- 1. RÉSUMÉ EXÉCUTIF -->
  <h2>1. ${tx.executiveSummary}</h2>
  
  <h3>${tx.projectHealthLevel}</h3>
  <div class="score-box">
    <div style="margin-bottom: 10pt;">${tx.globalScore}</div>
    <div class="score-value">${globalScore} / 5</div>
    <div style="margin-top: 10pt; font-size: 14pt; font-weight: bold; color: ${executiveSummary.classification.color};">
      ${executiveSummary.classification.icon} ${executiveSummary.classification.label}
    </div>
  </div>

  <h3>📊 ${tx.majorFindings}</h3>
  <div class="alert-box alert-blue">
    <ol>
      ${recommendations.slice(0, 3).map(rec => `
        <li><strong>${rec.axis}</strong> : ${rec.problem}</li>
      `).join('')}
    </ol>
  </div>

  <h3>🚨 ${tx.priorityRisks}</h3>
  ${executiveSummary.topRisks.slice(0, 3).map((risk, index) => `
    <div class="alert-box alert-red">
      <strong>${tx.risk} n°${index + 1} : ${risk.axis} (Score: ${risk.score})</strong><br>
      ${risk.problems ? risk.problems.risk : ''}
    </div>
  `).join('')}

  <h3>✓ ${tx.immediateDecisions}</h3>
  <div class="alert-box alert-green">
    <ol>
      ${recommendations.slice(0, 3).map((rec, index) => `
        <li><strong>${tx.decision} ${index + 1}</strong> : ${rec.action}</li>
      `).join('')}
    </ol>
  </div>

  <div class="page-break"></div>

  <!-- 2. MÉTHODOLOGIE -->
  <h2>2. ${tx.methodology}</h2>
  <ul>
    <li><strong>${tx.toolUsed} :</strong> ${tx.structuredQuestionnaire}</li>
    <li><strong>${tx.numberOfRespondents} :</strong> ${lang === 'fr' ? 'Chef de projet' : 'Project manager'} + ${diagnostic.participants - 1} ${lang === 'fr' ? 'personnes' : 'people'}</li>
    <li><strong>${tx.analysisMode} :</strong> ${tx.quantitativeQualitative}</li>
  </ul>
  <div class="alert-box alert-yellow">
    ⚠️ ${tx.methodologyNote}
  </div>

  <!-- 3. RÉSULTATS CHIFFRÉS -->
  <h2>3. ${tx.numericalResults}</h2>
  
  <table>
    <thead>
      <tr>
        <th>${tx.axis}</th>
        <th>${tx.averageScore}</th>
        <th>${tx.assessment}</th>
      </tr>
    </thead>
    <tbody>
      ${analysisResults.map(axis => `
        <tr>
          <td><strong>${axis.name}</strong></td>
          <td style="text-align: center; font-weight: bold; color: #0284c7;">${axis.score}</td>
          <td>${axis.classification.icon} ${axis.classification.label}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>

  <!-- Graphe à barres -->
  <div style="border: 1pt solid #cbd5e1; border-radius: 8pt; padding: 20pt; margin: 20pt 0; background-color: white; page-break-inside: avoid;">
    <h4 style="text-align: center; margin-bottom: 20pt;">${tx.scoreVisualization}</h4>
    
    <div style="position: relative; height: 350pt; margin-bottom: 20pt;">
      <!-- Grille horizontale -->
      <div style="position: absolute; left: 30pt; right: 0; top: 0; bottom: 40pt;">
        <table style="width: 100%; height: 100%; border: none; margin: 0;">
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">5</span></td></tr>
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">4</span></td></tr>
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">3</span></td></tr>
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">2</span></td></tr>
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">1</span></td></tr>
          <tr><td style="border: none; border-bottom: 1pt solid #e2e8f0; padding: 2pt 0; position: relative;"><span style="position: absolute; left: -25pt; font-size: 8pt; color: #64748b;">0</span></td></tr>
        </table>
      </div>
      
      <!-- Barres -->
      <div style="position: absolute; left: 35pt; right: 10pt; bottom: 40pt; height: 290pt;">
        <table style="width: 100%; height: 100%; border: none; margin: 0; border-collapse: separate; border-spacing: 5pt 0;">
          <tr style="vertical-align: bottom; height: 100%;">
            ${analysisResults.map(axis => {
              const heightPct = (parseFloat(axis.score) / 5) * 100;
              const barColor = 
                axis.classification.level === 'critical' ? '#ef4444' :
                axis.classification.level === 'fragile' ? '#f97316' :
                '#10b981';
              
              return `
                <td style="border: none; padding: 0; text-align: center; vertical-align: bottom; width: ${100/analysisResults.length}%;">
                  <div style="font-size: 8pt; font-weight: bold; margin-bottom: 3pt; color: #1e293b;">${axis.score}</div>
                  <div style="background-color: ${barColor}; border-radius: 4pt 4pt 0 0; height: ${heightPct}%; width: 100%;"></div>
                </td>
              `;
            }).join('')}
          </tr>
        </table>
      </div>
      
      <!-- Labels des axes -->
      <div style="position: absolute; left: 35pt; right: 10pt; bottom: 0; height: 35pt;">
        <table style="width: 100%; border: none; margin: 0; border-collapse: separate; border-spacing: 5pt 0;">
          <tr>
            ${analysisResults.map(axis => {
              const axisWords = axis.name.split(' ');
              return `
                <td style="border: none; padding: 0; text-align: center; vertical-align: top; font-size: 7pt; font-weight: 500; color: #334155; line-height: 1.1; width: ${100/analysisResults.length}%;">
                  ${axisWords.join('<br>')}
                </td>
              `;
            }).join('')}
          </tr>
        </table>
      </div>
    </div>
    
    <!-- Légende -->
    <table style="width: auto; margin: 10pt auto 0; border: none; font-size: 9pt;">
      <tr>
        <td style="border: none; padding: 3pt 10pt;">
          <span style="display: inline-block; width: 12pt; height: 12pt; background-color: #10b981; border-radius: 2pt; vertical-align: middle; margin-right: 5pt;"></span>
          <span>${tx.mastered}</span>
        </td>
        <td style="border: none; padding: 3pt 10pt;">
          <span style="display: inline-block; width: 12pt; height: 12pt; background-color: #f97316; border-radius: 2pt; vertical-align: middle; margin-right: 5pt;"></span>
          <span>${tx.fragile}</span>
        </td>
        <td style="border: none; padding: 3pt 10pt;">
          <span style="display: inline-block; width: 12pt; height: 12pt; background-color: #ef4444; border-radius: 2pt; vertical-align: middle; margin-right: 5pt;"></span>
          <span>${tx.critical}</span>
        </td>
      </tr>
    </table>
  </div>

  <h4>${tx.globalReading}</h4>
  <ul>
    <li><strong>${tx.criticalAxes} :</strong> ${analysisResults.filter(a => a.classification.level === 'critical').map(a => a.name).join(', ') || tx.none}</li>
    <li><strong>${tx.fragileAxes} :</strong> ${analysisResults.filter(a => a.classification.level === 'fragile').map(a => a.name).join(', ') || tx.none}</li>
    <li><strong>${tx.masteredAxes} :</strong> ${analysisResults.filter(a => a.classification.level === 'mastered').map(a => a.name).join(', ') || tx.none}</li>
  </ul>

  <div class="page-break"></div>

  <!-- 4. ANALYSE DES DYSFONCTIONNEMENTS -->
  ${criticalAndFragileAxes.length > 0 ? `
    <h2>4. ${tx.dysfunctionAnalysis}</h2>
    <p>${tx.dysfunctionIntro}</p>
    
    <table>
      <thead>
        <tr>
          <th>${tx.axis}</th>
          <th>Score</th>
          <th>${tx.observedProblems}</th>
          <th>${tx.projectImpacts}</th>
        </tr>
      </thead>
      <tbody>
        ${criticalAndFragileAxes.map(axis => `
          <tr>
            <td><strong>${axis.name}</strong></td>
            <td style="text-align: center; font-weight: bold;">${axis.score}</td>
            <td>${axis.problems?.problem || '-'}</td>
            <td>${axis.problems?.risk || '-'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="alert-box alert-red">
      <h4>${tx.syntheticAnalysis}</h4>
      <ul>
        <li><strong>${tx.whatBlocksPerformance} :</strong> ${criticalAndFragileAxes.filter(a => a.classification.level === 'critical').map(a => a.name).join(', ') || tx.noMajorBlockage}</li>
        <li><strong>${tx.whatGeneratesStress} :</strong> ${criticalAndFragileAxes.find(a => a.id === 'climate')?.problems?.problem || tx.climateMastered}</li>
        <li><strong>${tx.whatDelaysDecisions} :</strong> ${criticalAndFragileAxes.find(a => a.id === 'governance')?.problems?.problem || tx.functionalGovernance}</li>
      </ul>
    </div>

    <div class="page-break"></div>
  ` : ''}

  <!-- 5. ANALYSE QUALITATIVE DÉTAILLÉE -->
  ${criticalAndFragileAxes.length > 0 ? `
    <h2>5. ${tx.detailedQualitativeAnalysis}</h2>
    <p>${tx.qualitativeAnalysisIntro}</p>
    
    <div class="alert-box alert-yellow">
      ⚠️ ${tx.rootCauseNote}
    </div>

    <table style="font-size: 9pt;">
      <thead>
        <tr>
          <th>${tx.keyQuestion}</th>
          <th>${tx.rootCause}</th>
          <th>${tx.observableConsequence}</th>
          <th>${tx.correctiveAction}</th>
        </tr>
      </thead>
      <tbody>
        ${recommendations.map(rec => `
          <tr>
            <td><strong>${rec.axis}</strong></td>
            <td class="highlight-yellow">${tx.toDefine}</td>
            <td>${rec.problem}</td>
            <td>${rec.action}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="page-break"></div>
  ` : ''}

  <!-- 6. PLAN D'ACTIONS PRIORISÉ -->
  <h2>6. ${tx.prioritizedActionPlan}</h2>
  <p><strong>${lang === 'fr' ? 'Principe' : 'Principle'} :</strong> ${tx.actionPlanPrinciple}</p>
  
  <div class="alert-box alert-yellow">
    ⚠️ ${tx.responsibleDeadlineNote}
  </div>

  <table style="font-size: 9pt;">
    <thead>
      <tr>
        <th>${tx.axis}</th>
        <th>${tx.priorityAction}</th>
        <th>${tx.responsible}</th>
        <th>${tx.deadline}</th>
        <th>${tx.successIndicator}</th>
      </tr>
    </thead>
    <tbody>
      ${recommendations.slice(0, 5).map(rec => {
        let indicator = '';
        const checkVision = lang === 'fr' ? 'Vision' : 'Vision';
        const checkPriorities = lang === 'fr' ? 'Priorités' : 'Priorities';
        const checkGovernance = lang === 'fr' ? 'Gouvernance' : 'Governance';
        const checkMeetings = lang === 'fr' ? 'Réunions' : 'Meetings';
        const checkExecution = lang === 'fr' ? 'Exécution' : 'Execution';
        const checkClimate = lang === 'fr' ? 'Climat' : 'Climate';
        
        if (rec.axis.includes(checkVision)) {
          indicator = lang === 'fr' ? 'Objectifs SMART validés et communiqués' : 'SMART objectives validated and communicated';
        } else if (rec.axis.includes(checkPriorities)) {
          indicator = lang === 'fr' ? 'Planning stabilisé et respecté' : 'Stabilized and respected planning';
        } else if (rec.axis.includes(checkGovernance)) {
          indicator = lang === 'fr' ? 'Décisions prises en moins de 48h' : 'Decisions made in less than 48h';
        } else if (rec.axis.includes(checkMeetings)) {
          indicator = lang === 'fr' ? 'Taux de satisfaction réunions > 80%' : 'Meeting satisfaction rate > 80%';
        } else if (rec.axis.includes(checkExecution)) {
          indicator = lang === 'fr' ? 'Écarts détectés sous 1 semaine' : 'Deviations detected within 1 week';
        } else if (rec.axis.includes(checkClimate)) {
          indicator = lang === 'fr' ? 'Baisse du stress ressenti (enquête)' : 'Decrease in perceived stress (survey)';
        }
        
        return `
          <tr>
            <td><strong>${rec.axis}</strong></td>
            <td>${rec.action}</td>
            <td class="highlight-yellow">${tx.toDefine}</td>
            <td class="highlight-yellow">${tx.toDefine}</td>
            <td>${indicator}</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>

  <div class="page-break"></div>

  <!-- 7. BÉNÉFICES ATTENDUS -->
  <h2>7. ${tx.expectedBenefits}</h2>
  <ul>
    <li>✓ <strong>${tx.priorityClarification}</strong> : ${tx.teamAligned}</li>
    <li>✓ <strong>${tx.fasterDecisions}</strong> : ${tx.streamlinedProcess}</li>
    <li>✓ <strong>${tx.productiveMeetings}</strong> : ${tx.optimizedCollectiveTime}</li>
    <li>✓ <strong>${tx.betterVisibility}</strong> : ${tx.clearSharedProgress}</li>
    <li>✓ <strong>${tx.reducedStress}</strong> : ${tx.calmWorkEnvironment}</li>
    <li>✓ <strong>${tx.restoredPerformance}</strong> : ${tx.measurableGains}</li>
  </ul>

  <!-- 8. RECOMMANDATIONS FINALES -->
  <h2>8. ${tx.finalRecommendations}</h2>
  <ul>
    <li><strong>${tx.recommendedFollowUpRhythm} :</strong> ${tx.monthly}</li>
    <li><strong>${tx.nextDiagnosticReview} :</strong> <em>${tx.toFillByPM}</em></li>
    <li><strong>${tx.successConditions} :</strong>
      <ul>
        <li>${tx.topManagementCommitment}</li>
        <li>${tx.pilotingDiscipline}</li>
        <li>${tx.decisionClarity}</li>
      </ul>
    </li>
  </ul>

  <!-- 9. CONCLUSION -->
  <h2>9. ${tx.conclusion}</h2>
  <p>
    ${tx.conclusionText1}
  </p>
  <p>
    ${tx.conclusionText2}
  </p>

</body>
</html>
  `;
};